import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import inquiryQuotationService from '../services/inquiry-quotation.service';


export const fetchInquiryQuotationList = createAsyncThunk(
  'inquiryQuotation/fetchInquiryQuotationList',
  async ({ page, pagesize, search, groupId, ordering }, thunkAPI) => {
    try {
      const response = await inquiryQuotationService.getInquiryQuotationList(page, pagesize, search, groupId, ordering);
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const fetchArchivedInquiryQuotationList = createAsyncThunk(
  'inquiryQuotation/fetchArchivedInquiryQuotationList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await inquiryQuotationService.getInquiryArchiveList(page, pagesize, search, ordering);
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  },
);
export const fetchInquiryQuotationById = createAsyncThunk(
  'inquiryQuotation/fetchInquiryQuotationById',
  async (quotationId, { rejectWithValue }) => {
    try {
      const response = await inquiryQuotationService.getInquiryQuotationById(quotationId);
      return response.data; // Return the profile data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const convertInquiryToQuotation = createAsyncThunk(
  'inquiryQuotation/convertInquiryToQuotation',
  async ({ id, values }, thunkAPI) => {
    try {
      const response = await inquiryQuotationService.convertToQuotation(id, values);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createInquiryQuotation = createAsyncThunk(
  'inquiryQuotation/createInquiryQuotation',
  async ({ payload }, thunkAPI) => {
    try {
      
      const response = await inquiryQuotationService.addInquiryQuotation(payload);
      return response.data;
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateInquiryQuotation = createAsyncThunk(
  'inquiryQuotation/updateInquiryQuotation',
  async ({ id, values }, thunkAPI) => {
    try {
      const response = await inquiryQuotationService.editInquiryQuotation(id, values);
      return response.data; // Return the updated customer data
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteInquiryQuotation = createAsyncThunk(
  'inquiryQuotation/deleteInquiryQuotation',
  async (id, thunkAPI) => {
    try {
      await inquiryQuotationService.deleteInquiryQuotation(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const unArchiveInquiryQuotation = createAsyncThunk(
  'inquiryQuotation/unArchiveInquiryQuotation',
  async (id, thunkAPI) => {
    try {
      await inquiryQuotationService.unArchiveInquiryRecord(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteInquiryDetailById = createAsyncThunk(
  'inquiryQuotation/deleteInquiryDetails',
  async (id, thunkAPI) => {
    try {
      await inquiryQuotationService.deleteInquiryDetails(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  data: [],
  archiveData: [],
  totalPages: 0,
  currentPage: 1,
  totalRecord: 0,
  error: null,
  loading: false,
  hasMore: true,
};

const inquiryQuotationSlice = createSlice({
  name: 'inquiryQuotation',
  initialState,
  reducers: {
    resetInquiryData: (state) => {
      state.data = []; // This will clear the list
    },
    resetArchiveData: (state) => {
      state.archiveData = []; // This will clear the list
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchInquiryQuotationList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInquiryQuotationList.fulfilled, (state, action) => {
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.hasMore = !!action.payload.next;
        state.loading = false;
      })
      .addCase(fetchInquiryQuotationList.rejected, (state, action) => {
        state.loading = false;
      });
    builder
      .addCase(fetchArchivedInquiryQuotationList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchArchivedInquiryQuotationList.fulfilled, (state, action) => {
        state.status = 'succeeded';

        state.archiveData = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.hasMore = !!action.payload.next;
        state.loading = false;
      })
      .addCase(fetchArchivedInquiryQuotationList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
      });
    builder
      .addCase(fetchInquiryQuotationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiryQuotationById.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchInquiryQuotationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
       builder
      .addCase(convertInquiryToQuotation.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(convertInquiryToQuotation.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(convertInquiryToQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
    builder
      .addCase(createInquiryQuotation.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInquiryQuotation.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createInquiryQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
    builder
      .addCase(updateInquiryQuotation.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInquiryQuotation.fulfilled, (state, action) => {
        state.loading = false;

      })
      .addCase(updateInquiryQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
    builder
      .addCase(deleteInquiryQuotation.pending, state => {
        state.loading = true;
      })
      .addCase(deleteInquiryQuotation.fulfilled, (state, action) => {
        state.data = state.data.filter(group => group.id !== action?.meta?.arg); // Remove the deleted group from state
        state.totalRecord = state.totalRecord - 1;
        state.loading = false;
      })
      .addCase(deleteInquiryQuotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(unArchiveInquiryQuotation.pending, state => {
        state.loading = true;
      })
      .addCase(unArchiveInquiryQuotation.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.archiveData = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
         state.loading = false;
      })
      .addCase(unArchiveInquiryQuotation.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
  },
});


export const { resetInquiryData, resetArchiveData } = inquiryQuotationSlice.actions;
export default inquiryQuotationSlice.reducer;


